import type { ApiClient } from '@carbonwebmail/api-client'
import type { SegmentDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { Checkbox } from '../../ui/Checkbox.js'
import { Dialog } from '../../ui/Dialog.js'
import { Field } from '../../ui/Field.js'
import { ContactFields, STATES } from './ContactFields.js'
import type { NewContactValues } from './useNewContact.js'
import { EMPTY_CONTACT, useNewContact } from './useNewContact.js'

/**
 * Adding one contact, over the board rather than instead of it.
 *
 * It was a screen at `/contacts/new`, which meant leaving the list to add to it
 * and coming back to find it exactly as it was. A dialog keeps the rows behind
 * it and the board reloads when a row is genuinely inserted — the thing
 * somebody came here to see is the new contact in the table.
 *
 * **Every field on one form**: the built-in columns, the subscription state,
 * the segments to put them in, and every field this organisation declared. The
 * fields themselves are `ContactFields`, shared with the dialog that edits one,
 * because both ask for the same thing and two copies is two places for a
 * declared field to stop appearing.
 *
 * **It is the same path as an import of one row.** Adding somebody to a list is
 * the same assertion however it is done — that whoever added them holds their
 * consent — and it leaves the same provenance in the consent log. The state and
 * the segments travel with the row, so the contact and the audience somebody
 * put them in are one transaction rather than two that can half-happen.
 *
 * **An address already on the list is not overwritten**, and this says so
 * rather than reporting success. Their row carries whether they unsubscribed,
 * and a form that quietly reset that is how a product mails people who asked it
 * not to.
 */
export function NewContactDialog({
  client,
  segments,
  onAdded,
  onClose,
}: {
  readonly client: ApiClient
  /** Every segment the organisation has, which is what can be connected to. */
  readonly segments: readonly SegmentDto[]
  readonly onAdded: () => void
  readonly onClose: () => void
}) {
  const { fields, outcome, add, again } = useNewContact({ client, onAdded })
  const [values, setValues] = useState<NewContactValues>(EMPTY_CONTACT)

  const busy = outcome.status === 'adding'

  const set = <K extends keyof NewContactValues>(key: K, value: NewContactValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }))

  if (outcome.status === 'added' || outcome.status === 'already-there') {
    return (
      <Dialog
        footer={
          <>
            <button
              className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[13px] text-brand-contrast"
              onClick={() => {
                setValues(EMPTY_CONTACT)
                again()
              }}
              type="button"
            >
              Add another
            </button>
            <button
              className="font-medium text-[13px] text-t3 hover:text-t1"
              onClick={onClose}
              type="button"
            >
              Done
            </button>
          </>
        }
        heading={outcome.status === 'added' ? 'Added' : 'Already on your list'}
        onClose={onClose}
      >
        <p className="text-[13px] text-t2">
          {outcome.status === 'already-there'
            ? `${outcome.email} was already there, so nothing was changed — including whether they had unsubscribed, and which segments they are in.`
            : `${outcome.email} is on your list.`}
        </p>
      </Dialog>
    )
  }

  return (
    <Dialog
      footer={
        <>
          <button
            className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
            disabled={busy || values.email.trim() === ''}
            form="new-contact"
            type="submit"
          >
            {busy ? 'Adding…' : 'Add contact'}
          </button>
          <button
            className="font-medium text-[13px] text-t3 hover:text-t1"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
        </>
      }
      heading="New contact"
      onClose={onClose}
    >
      {outcome.status === 'refused' ? <Alert>{outcome.detail}</Alert> : null}

      <form
        id="new-contact"
        onSubmit={(event) => {
          event.preventDefault()
          void add(values)
        }}
      >
        {/* The one field the edit form does not have, so it lives here. */}
        <Field
          disabled={busy}
          hint="The only one required. Everything else can be filled in later."
          id="new-contact-email"
          label="Email address"
          onChange={(value) => set('email', value)}
          type="email"
          value={values.email}
        />

        <ContactFields
          busy={busy}
          company={values.company}
          custom={values.custom}
          fields={fields}
          idPrefix="new-contact"
          name={values.name}
          onCompany={(value) => set('company', value)}
          onCustom={(key, value) => set('custom', { ...values.custom, [key]: value })}
          onName={(value) => set('name', value)}
          onStatus={(value) => set('status', value)}
          status={values.status}
          statuses={STATES}
        />

        <Segments
          chosen={values.segmentIds}
          onToggle={(segmentId) =>
            set(
              'segmentIds',
              values.segmentIds.includes(segmentId)
                ? values.segmentIds.filter((one) => one !== segmentId)
                : [...values.segmentIds, segmentId],
            )
          }
          segments={segments}
        />

        <p className="mt-5 border-line border-t pt-4 text-[12px] text-t3">
          Nothing here says they agreed — the consent record says who added them and when, which is
          provenance rather than proof.
        </p>
      </form>
    </Dialog>
  )
}

/**
 * Which segments to put them in.
 *
 * Tick boxes rather than a picker, because this is the one moment somebody
 * knows every audience a new contact belongs to, and choosing them now costs
 * nothing. They are written in the same transaction as the row.
 *
 * Only on the way in. Changing them afterwards is the Segments cell on the row,
 * which is where somebody already looks for it — a second control for one fact
 * would be two places to disagree about it.
 */
function Segments({
  segments,
  chosen,
  onToggle,
}: {
  readonly segments: readonly SegmentDto[]
  readonly chosen: readonly string[]
  readonly onToggle: (segmentId: string) => void
}) {
  if (segments.length === 0) return null

  return (
    <fieldset className="mt-4">
      <legend className="mb-1.5 font-semibold text-[12px] text-t2">Segments</legend>
      <div className="flex flex-col gap-2 rounded-field border border-line bg-surface p-3">
        {segments.map((segment) => (
          <span className="flex items-center gap-2" key={segment.segmentId}>
            <Checkbox
              checked={chosen.includes(segment.segmentId)}
              id={`new-contact-segment-${segment.segmentId}`}
              label={segment.name}
              onChange={() => onToggle(segment.segmentId)}
            />
            <label
              className="cursor-pointer text-[13px] text-t1"
              htmlFor={`new-contact-segment-${segment.segmentId}`}
            >
              {segment.name}
            </label>
          </span>
        ))}
      </div>
    </fieldset>
  )
}
